import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, PenLine, FileCheck2 } from "lucide-react";
import { Button } from "./ui/button";
import { paymentProvidersApi } from "../lib/api";

const MAX = 2000;

/** Company-wide Customer Sign-Off paragraphs. Two independent boxes because
 * there are two distinct signing moments in the workflow:
 *   1. "Estimate acceptance" — customer signs to authorize the proposed work.
 *   2. "Work complete" — customer signs on-site after the tech finishes.
 * Both paragraphs are baked into the corresponding PDF receipt that goes out
 * on email + shown above the pad in the mobile / public flows.
 */
export default function CustomerSignoffCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // "estimate" | "work" | null
  const [state, setState] = useState({
    estimateSignoffText: "",
    workCompleteSignoffText: "",
  });
  const [dirty, setDirty] = useState({ estimate: false, work: false });

  useEffect(() => {
    (async () => {
      try {
        const d = await paymentProvidersApi.getBehavior();
        setState({
          estimateSignoffText: d?.settings?.estimateSignoffText || "",
          workCompleteSignoffText: d?.settings?.workCompleteSignoffText || "",
        });
      } catch (err) {
        toast.error("Could not load sign-off paragraphs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (which) => {
    setSaving(which);
    try {
      const payload =
        which === "estimate"
          ? { estimateSignoffText: state.estimateSignoffText }
          : { workCompleteSignoffText: state.workCompleteSignoffText };
      const d = await paymentProvidersApi.saveBehavior(payload);
      setState((s) => ({
        ...s,
        estimateSignoffText: d?.settings?.estimateSignoffText ?? s.estimateSignoffText,
        workCompleteSignoffText: d?.settings?.workCompleteSignoffText ?? s.workCompleteSignoffText,
      }));
      setDirty((prev) => ({ ...prev, [which]: false }));
      toast.success(`${which === "estimate" ? "Estimate" : "Work-complete"} paragraph saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="signoff-card-loading">
        <div className="text-center py-6 text-slate-400">
          <Loader2 className="h-4 w-4 inline animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="signoff-card">
      <div className="flex items-center gap-2 mb-1">
        <PenLine className="h-4 w-4 text-indigo-600" />
        <h2 className="text-[14px] font-semibold text-slate-900">Customer Sign-Off Paragraphs</h2>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        These paragraphs appear above the signature pad — one when a customer accepts an estimate, one when they sign off on completed work. Both are baked into the PDF receipt emailed to the customer.
      </p>

      <SignoffTextarea
        label="Estimate acceptance"
        subtitle="Shown above the customer's signature on the estimate acceptance page and printed on the estimate PDF."
        icon={FileCheck2}
        testid="signoff-estimate"
        value={state.estimateSignoffText}
        onChange={(v) => {
          setState((s) => ({ ...s, estimateSignoffText: v }));
          setDirty((d) => ({ ...d, estimate: true }));
        }}
        onSave={() => save("estimate")}
        saving={saving === "estimate"}
        dirty={dirty.estimate}
      />

      <div className="h-3" />

      <SignoffTextarea
        label="Work completion"
        subtitle="Shown above the signature pad on the mobile Sign-Off step and printed on the invoice/receipt PDF."
        icon={PenLine}
        testid="signoff-work"
        value={state.workCompleteSignoffText}
        onChange={(v) => {
          setState((s) => ({ ...s, workCompleteSignoffText: v }));
          setDirty((d) => ({ ...d, work: true }));
        }}
        onSave={() => save("work")}
        saving={saving === "work"}
        dirty={dirty.work}
      />
    </div>
  );
}

function SignoffTextarea({ label, subtitle, icon: Icon, testid, value, onChange, onSave, saving, dirty }) {
  const remaining = MAX - (value?.length || 0);
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`${testid}-block`}>
      <div className="flex items-start gap-2 mb-1.5">
        <Icon className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-slate-900">{label}</div>
          <div className="text-[11.5px] text-slate-500">{subtitle}</div>
        </div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, MAX))}
        data-testid={`${testid}-input`}
        rows={4}
        placeholder="Write the paragraph the customer will be agreeing to when they sign…"
        className="w-full rounded-md border border-slate-300 bg-white text-[12.5px] text-slate-800 leading-relaxed p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400 resize-y overflow-y-auto"
        style={{ minHeight: "5.5rem", maxHeight: "18rem" }}
      />
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-400" data-testid={`${testid}-count`}>
          {remaining} character{remaining === 1 ? "" : "s"} remaining
        </div>
        <Button
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid={`${testid}-save`}
          className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-[12.5px] font-semibold px-3 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}
